import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  Form,
  FormButton,
  Input,
  SwitchInput,
  useNotifications
} from '@ijoin/components';
import {
  AnnuityCarrierTransactionRes,
  getGetAnnuityCarrierTransactionsQueryKey,
  usePatchAnnuityCarrierTransaction
} from '@ijoin/hooks';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ApiError } from '../types';

type FormValues = {
  reversalIndicator: boolean;
  reversesId: string;
};

export const MarkAsReversalDialog: FC<{
  annuityIntegrationId: string;
  onClose: () => void;
  transaction: AnnuityCarrierTransactionRes;
}> = props => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const methods = useForm<FormValues>({
    defaultValues: {
      reversalIndicator: props.transaction.reversalIndicator,
      reversesId: props.transaction.reversesId ?? ''
    },
    mode: 'all',
    resolver: zodResolver(
      z
        .object({
          reversalIndicator: z.boolean(),
          reversesId: z.string()
        })
        .superRefine((values, ctx) => {
          if (!values.reversalIndicator) {
            return;
          }

          if (!values.reversesId.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Original transaction ID is required',
              path: ['reversesId']
            });
            return;
          }

          if (!/^\d+$/.test(values.reversesId.trim())) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Transaction ID must be numeric',
              path: ['reversesId']
            });
            return;
          }

          if (values.reversesId.trim() === String(props.transaction.id)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'A transaction cannot reverse itself',
              path: ['reversesId']
            });
          }
        })
    )
  });

  const reversalIndicator = methods.watch('reversalIndicator');

  const updateTransaction = usePatchAnnuityCarrierTransaction<ApiError>({
    mutation: {
      onError: error => {
        if (error.status === 400) {
          methods.setError('reversesId', {
            message: error.message,
            type: 'manual'
          });
          return;
        }

        notifications.error(error.message || 'Failed to update transaction');
      },
      onSuccess: () => {
        notifications.success('Transaction updated');
        queryClient.invalidateQueries({
          queryKey: getGetAnnuityCarrierTransactionsQueryKey(
            props.annuityIntegrationId
          )
        });
        props.onClose();
      }
    }
  });

  const onReversalChange = () => methods.trigger('reversesId');

  const onSubmit = (data: FormValues) => {
    updateTransaction.mutate({
      annuityIntegrationId: props.annuityIntegrationId,
      data: {
        reversalIndicator: data.reversalIndicator,
        reversesId: data.reversalIndicator ? data.reversesId.trim() : undefined
      },
      id: props.transaction.id
    });
  };

  return (
    <Dialog
      data-testid={`markAsReversal-${props.transaction.id}`}
      fullWidth
      isOpen
      maxWidth='xs'
      onClose={props.onClose}>
      <Form
        methods={methods}
        noValidate
        onSubmit={methods.handleSubmit(onSubmit)}>
        <DialogTitle variant='h3'>Mark as Reversal</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SwitchInput
              label='Reversal'
              name='reversalIndicator'
              onChange={onReversalChange}
              tooltip='Indicate if this transaction reverses a previous one for the same participant.'
              tooltipIcon={<InfoOutlined />}
            />
            {reversalIndicator && (
              <Input
                format='string'
                fullWidth
                label='Original Transaction ID'
                name='reversesId'
                required
                size='small'
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid={`cancelMarkAsReversal-${props.transaction.id}`}
            onClick={props.onClose}
            variant='outlined'>
            Cancel
          </Button>
          <FormButton
            data-testid={`saveMarkAsReversal-${props.transaction.id}`}
            triggerValidation
            variant='contained'>
            Save
          </FormButton>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

MarkAsReversalDialog.displayName = 'MarkAsReversalDialog';
